/**
 * FeatureCard Component
 *
 * Displays a single feature in a card format with type badge, confidence score, and file paths
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { FileCode } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Feature, FeatureCategory } from '@forgenetwork/types';

interface FeatureCardProps {
  feature: Feature;
  onClick?: (feature: Feature) => void;
  className?: string;
}

/**
 * Category-specific styling for feature badges
 */
const categoryColors: Record<FeatureCategory, string> = {
  authentication: 'bg-red-100 text-red-800 hover:bg-red-200',
  authorization: 'bg-pink-100 text-pink-800 hover:bg-pink-200',
  'data-storage': 'bg-green-100 text-green-800 hover:bg-green-200',
  api: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
  ui: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
  'business-logic': 'bg-orange-100 text-orange-800 hover:bg-orange-200',
  integration: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
  infrastructure: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
  testing: 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200',
  other: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
};

/**
 * Complexity badge variants
 */
const complexityVariants: Record<string, 'default' | 'secondary' | 'destructive'> = {
  low: 'secondary',
  medium: 'default',
  high: 'destructive',
};

/**
 * Format category name for display
 */
function formatCategory(category: FeatureCategory): string {
  return category
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * FeatureCard - Display a feature with its metadata
 */
export function FeatureCard({ feature, onClick, className }: FeatureCardProps): React.JSX.Element {
  const confidencePercentage = Math.round(feature.confidence * 100);

  return (
    <Card
      className={cn(
        'transition-all hover:shadow-md',
        onClick && 'cursor-pointer hover:border-primary',
        className
      )}
      onClick={() => onClick?.(feature)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg font-semibold leading-tight">
            {feature.name}
          </CardTitle>
          <Badge
            className={cn(
              'shrink-0 font-medium',
              categoryColors[feature.category]
            )}
          >
            {formatCategory(feature.category)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2">
          {feature.description}
        </p>

        {/* Confidence Score */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Confidence</span>
            <span className="font-medium">{confidencePercentage}%</span>
          </div>
          <Progress value={confidencePercentage} className="h-2" />
        </div>

        {/* Complexity Badge */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Complexity</span>
          <Badge variant={complexityVariants[feature.complexity]}>
            {feature.complexity.toUpperCase()}
          </Badge>
        </div>

        {/* File Count */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2 border-t">
          <FileCode className="h-4 w-4" />
          <span>
            {feature.files.length} {feature.files.length === 1 ? 'file' : 'files'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
